import React from "react";
import Link from "next/link";
import Image from "next/image";
import { NewContainer } from "../NewContainer";

const teamMembers = [
  {
    name: "Ledgerowl AI Engine",
    role: "Processes 10,000+ transactions per hour. Auto-categorises, reconciles & flags anomalies.",
    type: "ai",
    avatar: "bolt",
  },
  {
    name: "Oscar Syarief, CPA",
    role: "COO · 20+ years experience in Australia, Singapore and Indonesia · ex-JLL",
    type: "human",
    initials: "OS",
    image: "/team/oscar.svg",
  },
  {
    name: "Rey Kamal, CA, CPA",
    role: "Co-Founder & CEO · 17+ years experience in Accounting & Tax in Australia and Indonesia",
    type: "human",
    initials: "RK",
    image: "/team/rey.svg",
  },
  {
    name: "Anomaly Detection AI",
    role: "Monitors transactions 24/7. Flags duplicate charges, unusual patterns & missing invoices.",
    type: "ai",
    avatar: "eye",
  },
  {
    name: "Kim Pierce",
    role: "Dedicated Account Manager · Your single point of contact for all things financial.",
    type: "human",
    initials: "KP",
    image: "/team/kim.svg",
  },
];

const AiAvatar = ({ icon }) => (
  <div className="w-[60px] h-[60px] rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-blue-primary-500 to-[#6366F1] text-white text-[24px]">
    {icon === "bolt" ? (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
        <path
          fillRule="evenodd"
          d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
          clipRule="evenodd"
        />
      </svg>
    )}
  </div>
);

const HumanAvatar = ({ initials, image }) =>
  image ? (
    <Image
      src={image}
      alt={initials}
      width={60}
      height={60}
      className="w-[60px] h-[60px] rounded-full flex-shrink-0 object-cover"
    />
  ) : (
    <div className="w-[60px] h-[60px] rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F1D2F] text-white font-bold text-[18px]">
      {initials}
    </div>
  );

export const NewSection = () => {
  return (
    <section id="team" className="relative py-[120px] px-6 lg:px-10 bg-blue-primary-900 overflow-hidden">
      {/* Radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[radial-gradient(circle,rgba(0,56,112,0.5),transparent_70%)]" />

      <div className="relative z-10 max-w-[1340px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        {/* Left: Content */}
        <div className="fade-in">
          <p className="text-[13px] font-semibold text-blue-primary-400 mb-4 tracking-wide">
            Meet your team
          </p>
          <h2 className="font-inter text-[36px] leading-[46px] font-extrabold tracking-[-1.5px] mb-5 text-white">
            Real experts behind the technology
          </h2>
          <p className="font-inter text-[17px] leading-[1.8] text-white/70 mb-8">
            Ledgerowl combines AI automation with oversight from qualified CAs and CPAs. Real professionals review and validate the numbers, ensuring your financials are accurate, compliant, and reliable.
          </p>
          <div className="flex gap-4 flex-wrap">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-blue-primary-500 hover:bg-[#005bb5] text-white px-9 py-4 rounded-full text-base font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,116,230,0.35)]"
            >
              Meet All Our Experts
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </div>
        </div>

        {/* Right: Team cards */}
        <div className="flex flex-col gap-5 fade-in">
          {teamMembers.map((member) => (
            <div
              key={member.name}
              className="bg-blue-primary-900 rounded-[18px] p-7 px-8 flex items-center gap-5 border border-white/[0.1] transition-all duration-300 hover:translate-x-1.5 hover:border-white/[0.2]"
            >
              {member.type === "ai" ? (
                <AiAvatar icon={member.avatar} />
              ) : (
                <HumanAvatar initials={member.initials} image={member.image} />
              )}
              <div className="min-w-0 flex-1">
                <h4 className="font-inter text-base font-bold mb-0.5 text-white">
                  {member.name}
                </h4>
                <p className="font-inter text-[13px] text-white/60 leading-[1.5]">
                  {member.role}
                </p>
              </div>
              <span
                className={`text-[11px] px-3 py-1 rounded-full font-bold uppercase tracking-[0.5px] flex-shrink-0 ${
                  member.type === "ai"
                    ? "bg-blue-primary-500/[0.15] text-blue-primary-400"
                    : "bg-textNoConnection/[0.12] text-textNoConnection"
                }`}
              >
                {member.type === "ai" ? "AI" : "Human"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
